const DEMO_COURSES = [
  { slug: 'photo', title: 'Photography Masterclass', category: 'Creative' },
  { slug: 'python', title: 'Python for Beginners', category: 'Programming' },
  { slug: 'excel', title: 'Excel for Professionals', category: 'Office' },
  { slug: 'graphic-design', title: 'Graphic Design Fundamentals', category: 'Creative' },
  { slug: 'digital-marketing', title: 'Digital Marketing Bootcamp', category: 'Marketing' },
  { slug: 'web-dev', title: 'Web Development Zero to Hero', category: 'Programming' },
  { slug: 'data-science', title: 'Data Science with Python', category: 'Programming' },
  { slug: 'ui-ux', title: 'UI/UX Design Essentials', category: 'Design' },
  { slug: 'video-editing', title: 'Video Editing with Premiere', category: 'Creative' },
  { slug: 'freelancing', title: 'Freelancing in Bangladesh', category: 'Career' },
  { slug: 'spoken-english', title: 'Spoken English Fluency', category: 'Language' },
  { slug: 'cybersecurity', title: 'Cybersecurity Basics', category: 'IT' },
] as const;

const DEMO_INSTRUCTORS = [
  { id: 'ins_1', name: 'Ayesha Karim' },
  { id: 'ins_2', name: 'Tanvir Hasan' },
  { id: 'ins_3', name: 'Imran Chowdhury' },
] as const;

function iso(daysAgo = 0): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString();
}

function isoLiveClass(daysAhead: number): string {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  d.setHours(20, 0, 0, 0);
  return d.toISOString();
}

export type OrderStatus = 'paid' | 'refund_requested' | 'refunded' | 'refund_denied';

export interface OrderLineItem {
  title: string;
  price: number;
}

export interface OrderRefundRequest {
  reason: string;
  details: string;
  requestedAt: string;
}

export interface DemoOrder {
  orderId: string;
  customerName: string;
  email: string;
  items: OrderLineItem[];
  total: number;
  method: string;
  status: OrderStatus;
  createdAt: string;
  paidAt: string;
  txnRef?: string;
  refundRequest?: OrderRefundRequest | null;
  refundedAt?: string;
  refundDeniedAt?: string;
}

export interface DemoBook {
  slug: string;
  title: string;
  author: string;
  price: number;
  oldPrice: number | null;
  category: string;
  stock: number;
  inStock: boolean;
  pages: number;
  createdAt: string;
}

export type BookOrderStatus = 'pending' | 'processing' | 'packed' | 'shipped' | 'delivered' | 'cancelled';

export interface DemoBookOrderItem {
  title: string;
  qty: number;
  unitPrice: number;
}

export interface DemoBookOrder {
  orderId: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  district: string;
  items: DemoBookOrderItem[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  paymentMethod: string;
  status: BookOrderStatus;
  notes?: string;
  orderedAt: string;
  shippedAt?: string;
  deliveredAt?: string;
}

export type PayoutStatus = 'pending' | 'processing' | 'paid';

export interface DemoPayoutCourse {
  title: string;
  sales: number;
  share: number;
}

export interface DemoPayout {
  instructorId: string;
  instructor: string;
  email: string;
  period: string;
  gross: number;
  platform: number;
  instructorShare: number;
  status: PayoutStatus;
  paymentMethod: string;
  payoutAccount: string;
  courses: DemoPayoutCourse[];
  paidAt?: string;
  reference?: string;
}

export type CouponDiscountType = 'percent' | 'flat';
/** Who may use this coupon at checkout. */
export type CouponAudience = 'public' | 'affiliate';

export interface DemoCoupon {
  code: string;
  discountType: CouponDiscountType;
  /** Percent 1–100, or flat amount in poisha (minor units). */
  value: number;
  /** null = all courses */
  courseSlug: string | null;
  courseTitle: string | null;
  /** public = anyone; affiliate = only with an active affiliate ?ref= */
  audience: CouponAudience;
  expiresAt: string | null;
  active: boolean;
  createdAt: string;
}

export type ReviewStatus = 'published' | 'hidden' | 'flagged';

export interface DemoReview {
  id: string;
  name: string;
  email: string;
  rating: number;
  body: string;
  course: string;
  courseSlug: string;
  status: ReviewStatus;
  createdAt: string;
}

export type BusinessLeadStatus = 'new' | 'contacted' | 'visited' | 'closed';

export interface DemoBusinessLead {
  id: string;
  name: string;
  company: string;
  email: string;
  teamSize: string;
  status: BusinessLeadStatus;
  createdAt: string;
}

export interface DemoFreeResource {
  id: string;
  title: string;
  description: string;
  category: string;
  fileName: string;
  /** Remote URL or data: URL for the PDF */
  pdfUrl: string;
  downloads: number;
  createdAt: string;
  updatedAt: string;
}

/** How the instructor joined the platform */
export type InstructorSource = 'admin' | 'application';
/** Course access granted to the instructor */
export type InstructorCourseAccess = 'all' | 'selected' | 'own';
export type InstructorRecordStatus = 'active' | 'pending' | 'rejected';

export interface InstructorPaymentMethods {
  bank: {
    bankName: string;
    accountName: string;
    accountNumber: string;
    routingNumber: string;
  } | null;
  bkash: string | null;
  nagad: string | null;
  rocket: string | null;
}

export interface DemoInstructor {
  id: string;
  name: string;
  email: string;
  source: InstructorSource;
  status: InstructorRecordStatus;
  courseAccess: InstructorCourseAccess;
  /** Populated when courseAccess === 'selected' (or own with known courses) */
  courseSlugs: string[];
  courseTitles: string[];
  /** Topic from application form (applied instructors) */
  topic: string | null;
  suspended: boolean;
  createdAt: string;
  approvedAt?: string | null;
  /** Panel profile fields (editable by instructor) */
  phone?: string | null;
  bio?: string | null;
  headline?: string | null;
  /** Payout accounts — used by applied instructors on Payment page */
  paymentMethods?: InstructorPaymentMethods;
  /** Available to withdraw (poisha). Application instructors only. */
  availableBalance?: number;
}

export interface DemoAssignment {
  id: string;
  title: string;
  /** Plain-text preview for table rows */
  brief: string;
  /** Rich HTML instructions shown to students */
  instructionsHtml: string;
  course: string;
  courseSlug: string;
  dueDate: string | null;
  submissions: number;
  reviewed: number;
  createdAt: string;
}

export interface AuditEntry {
  action: string;
  meta: unknown;
  actor: string;
  role: string | null;
  createdAt: string;
}

export interface DemoStore {
  overview: {
    kpis: { revenue: number; students: number; enrolments: number; certificates: number; completionRate: number };
    revenueByCourse: { title: string; revenue: number }[];
    recentOrders: { orderId: string; email: string; total: number; status: string }[];
  };
  reports: {
    courses: {
      slug: string;
      title: string;
      category: string;
      enrolled: number;
      completed: number;
      completionRate: number;
      ratingAvg: number | null;
      reviewCount: number;
      revenue: number;
    }[];
  };
  courses: {
    courses: {
      slug: string;
      title: string;
      category: string;
      price: number;
      realStudents: number;
      instructorId: string;
      instructorName: string;
      hidden: boolean;
      isLive: boolean;
      nextLiveAt: string | null;
    }[];
  };
  /** Full course detail payloads for the public product page (demo store). */
  courseDetails: Record<string, Record<string, unknown>>;
  bundles: {
    bundles: {
      slug: string;
      title: string;
      icon: string | null;
      blurb: string;
      discountPct: number;
      full: number;
      now: number;
      courses: string[];
    }[];
  };
  qbankQuestions: Record<
    string,
    Record<
      string,
      { id: string; prompt: string; options: string[]; answerIndex: number; explanation: string; isPlaceholder: boolean }[]
    >
  >;
  enrollments: {
    enrollments: {
      userId: string;
      courseSlug: string;
      name: string;
      email: string;
      course: string;
      purchased: boolean;
      progressPct: number;
      certId: string | null;
    }[];
    students: { id: string; name: string; email: string }[];
    courses: { slug: string; title: string }[];
  };
  users: {
    users: { id: string; name: string; email: string; role: string; suspended: boolean; points: number; joinedAt: string }[];
  };
  staff: {
    staff: { id: string; name: string; email: string; role: string }[];
    roles: { key: string; label: string }[];
    matrix: { section: string; label: string; allowed: Record<string, boolean> }[];
  };
  orders: {
    orders: DemoOrder[];
  };
  books: {
    books: DemoBook[];
  };
  bookorders: {
    counts: Record<string, number>;
    orders: DemoBookOrder[];
  };
  payouts: {
    commissionPct: number;
    payouts: DemoPayout[];
  };
  coupons: { coupons: DemoCoupon[] };
  assignments: {
    reviewQueue: number;
    assignments: DemoAssignment[];
  };
  content: {
    articles: { slug: string; title: string; category: string; author: string; publishedAt: string }[];
  };
  downloads: {
    resources: { slug: string; title: string; category: string; type: string; downloads: number }[];
    leads: { name: string; email: string; phone: string | null; wanted: string | null; source: string; createdAt: string }[];
  };
  qa: {
    threads: {
      id: string;
      course: string;
      question: string;
      user: { name: string; email: string };
      replies: { id: string; body: string; staff: boolean; user: { name: string } }[];
    }[];
  };
  tickets: {
    counts: Record<string, number>;
    tickets: {
      id: string;
      ticketNo: string;
      subject: string;
      category: string;
      body: string;
      status: string;
      user: { name: string; email: string };
      replies: { id: string; body: string; staff: boolean; user: { name: string } }[];
    }[];
  };
  reviews: {
    reviews: DemoReview[];
  };
  instructors: {
    instructors: DemoInstructor[];
  };
  leads: {
    leads: DemoBusinessLead[];
  };
  freeresources: {
    resources: DemoFreeResource[];
  };
  /** @deprecated kept for older demo stores — prefer freeresources */
  pdfleads: {
    total: number;
    bySource: Record<string, number>;
    leads: { name: string; email: string; phone: string | null; wanted: string | null; source: string; createdAt: string }[];
  };
  counselling: {
    bookings: { id: string; name: string; phone: string; email: string; goal: string; slot: string; status: string; createdAt: string }[];
  };
  affiliates: {
    stats: { affiliates: number; referredEnrolments: number; commissionEarned: number; pendingWithdrawals: number };
    withdrawals: {
      id: string;
      name: string;
      email: string;
      phone: string;
      code: string;
      amount: number;
      method: 'bank' | 'bkash' | 'nagad' | 'rocket';
      account: string;
      status: 'pending' | 'paid';
      requestedAt: string;
    }[];
    /** Per-course commission ledger (affiliate sales). */
    sales: {
      id: string;
      affiliateId: string;
      affiliateName: string;
      affiliateCode: string;
      courseSlug: string;
      courseTitle: string;
      orderAmount: number;
      commission: number;
      commissionPct: number;
      couponCode: string | null;
      createdAt: string;
    }[];
    affiliates: {
      id: string;
      name: string;
      email: string;
      phone: string;
      code: string;
      clicks: number;
      signups: number;
      earned: number;
      available: number;
      joinedAt: string;
      status: 'active' | 'suspended';
      /** Panel profile fields */
      bio?: string | null;
      /** Same payout account shape as instructor Payment */
      paymentMethods?: InstructorPaymentMethods;
    }[];
  };
  live: {
    sessions: { id: string; course: string; title: string; startsAt: string }[];
    liveCourses: { slug: string; title: string }[];
  };
  masterclass: {
    webinars: { slug: string; title: string; category: string; type: string; startsAt: string | null; signups: number; isRecorded: boolean }[];
  };
  certs: {
    certificates: { certId: string; holder: string; course: string; issuedAt: string; revoked: boolean }[];
  };
  announce: {
    announcements: { id: string; title: string; body: string; createdAt: string }[];
    promotions: { id: string; text: string; active: boolean; createdAt: string }[];
    banner: { id: string; text: string; active: boolean } | null;
  };
  settings: {
    settings: { regOpen: boolean; maintenance: boolean; commissionPct: number; affiliateCommissionPct: number; supportEmail: string };
  };
  audit: { entries: AuditEntry[] };
}

export function createDemoSeed(): DemoStore {
  const reportCourses = DEMO_COURSES.map((c, i) => {
    const enrolled = 120 + i * 37;
    const completed = Math.round(enrolled * (0.45 + (i % 5) * 0.08));
    const completionRate = Math.round((completed / enrolled) * 100);
    return {
      slug: c.slug,
      title: c.title,
      category: c.category,
      enrolled,
      completed,
      completionRate,
      ratingAvg: 4.2 + (i % 4) * 0.15,
      reviewCount: 8 + i * 3,
      revenue: (enrolled * (2990 + i * 500)) * 100,
    };
  });

  const courseRows = DEMO_COURSES.map((c, i) => {
    const instructor = DEMO_INSTRUCTORS[i % DEMO_INSTRUCTORS.length]!;
    const isLive = i % 3 === 0;
    return {
      slug: c.slug,
      title: c.title,
      category: c.category,
      price: (2990 + i * 500) * 100,
      realStudents: 18 + i * 11,
      instructorId: instructor.id,
      instructorName: instructor.name,
      hidden: false,
      isLive,
      nextLiveAt: isLive ? isoLiveClass(3 + (i % 5)) : null,
    };
  });

  const students = [
    { id: 'u_sadia', name: 'Sadia Rahman', email: 'sadia@demo.com' },
    { id: 'u_rahim', name: 'Rahim Khan', email: 'rahim@demo.com' },
    { id: 'u_nusrat', name: 'Nusrat Jahan', email: 'nusrat@demo.com' },
    { id: 'u_karim', name: 'Karim Ahmed', email: 'karim@demo.com' },
  ];

  const tickets = [
    {
      id: 'tkt_1',
      ticketNo: 'TKT-1042',
      subject: 'Cannot access purchased course',
      category: 'Access',
      body: 'I paid yesterday but the course still shows locked on my dashboard.',
      status: 'open',
      user: { name: 'Sadia Rahman', email: 'sadia@demo.com' },
      replies: [],
    },
    {
      id: 'tkt_2',
      ticketNo: 'TKT-1038',
      subject: 'Certificate name spelling',
      category: 'Certificates',
      body: 'My certificate shows the wrong spelling of my name.',
      status: 'progress',
      user: { name: 'Rahim Khan', email: 'rahim@demo.com' },
      replies: [{ id: 'r1', body: 'We are checking with the certificate team.', staff: true, user: { name: 'Support' } }],
    },
    {
      id: 'tkt_3',
      ticketNo: 'TKT-1021',
      subject: 'Refund request',
      category: 'Billing',
      body: 'Please process a refund for order ORD-8821.',
      status: 'resolved',
      user: { name: 'Nusrat Jahan', email: 'nusrat@demo.com' },
      replies: [{ id: 'r2', body: 'Refund processed. Allow 3–5 business days.', staff: true, user: { name: 'Support' } }],
    },
    {
      id: 'tkt_4',
      ticketNo: 'TKT-1055',
      subject: 'Live class link not working',
      category: 'Live class',
      body: 'The Zoom link for tonight’s live class opens a blank page on my phone.',
      status: 'open',
      user: { name: 'Karim Ahmed', email: 'karim@demo.com' },
      replies: [],
    },
    {
      id: 'tkt_5',
      ticketNo: 'TKT-1048',
      subject: 'Wrong book shipped',
      category: 'Books',
      body: 'I ordered Lighting Handbook but received a different title.',
      status: 'progress',
      user: { name: 'Sadia Rahman', email: 'sadia@demo.com' },
      replies: [
        { id: 'r3', body: 'Sorry about that — please share a photo of the package label.', staff: true, user: { name: 'Support' } },
        { id: 'r4', body: 'Photo attached via email. Tracking was BK-9912.', staff: false, user: { name: 'Sadia Rahman' } },
      ],
    },
    {
      id: 'tkt_6',
      ticketNo: 'TKT-1010',
      subject: 'Coupon code not applying',
      category: 'Billing',
      body: 'MAK20 shows invalid at checkout even though it hasn’t expired.',
      status: 'resolved',
      user: { name: 'Rahim Khan', email: 'rahim@demo.com' },
      replies: [{ id: 'r5', body: 'Code was case-sensitive — try mak20. Issue fixed on our side too.', staff: true, user: { name: 'Support' } }],
    },
  ];

  const roles = [
    { key: 'superadmin', label: 'Super Admin' },
    { key: 'admin', label: 'Admin' },
    { key: 'instructor', label: 'Instructor' },
    { key: 'mentor', label: 'Mentor' },
    { key: 'support', label: 'Support Executive' },
    { key: 'order_manager', label: 'Order Manager' },
  ];

  const matrix = [
    'overview',
    'reports',
    'courses',
    'tickets',
    'qa',
    'staff',
    'settings',
    'audit',
  ].map((section) => ({
    section,
    label: section.charAt(0).toUpperCase() + section.slice(1),
    allowed: {
      admin: section !== 'settings',
      instructor: ['overview', 'courses', 'qa'].includes(section),
      mentor: ['overview', 'qa'].includes(section),
      support: ['overview', 'tickets', 'qa'].includes(section),
    },
  }));

  return {
    overview: {
      kpis: {
        revenue: 284500000,
        students: 4820,
        enrolments: 6310,
        certificates: 1890,
        completionRate: 62,
      },
      revenueByCourse: reportCourses.slice(0, 6).map((r) => ({ title: r.title, revenue: r.revenue })),
      recentOrders: [
        { orderId: 'ORD-9901', email: 'sadia@demo.com', total: 349000, status: 'paid' },
        { orderId: 'ORD-9898', email: 'rahim@demo.com', total: 499000, status: 'refund_requested' },
        { orderId: 'ORD-9892', email: 'nusrat@demo.com', total: 299000, status: 'pending' },
      ],
    },
    reports: { courses: reportCourses },
    courses: { courses: courseRows },
    courseDetails: {},
    bundles: {
      bundles: [
        {
          slug: 'creator-pack',
          title: 'Creator Pack',
          icon: 'palette',
          blurb: 'Photo, design and video — everything for creators.',
          discountPct: 15,
          full: 1058000,
          now: 899000,
          courses: ['Photography Masterclass', 'Graphic Design Fundamentals', 'Video Editing with Premiere'],
        },
        {
          slug: 'dev-starter',
          title: 'Developer Starter Bundle',
          icon: 'code',
          blurb: 'Python, web dev and data science in one track.',
          discountPct: 20,
          full: 1622000,
          now: 1299000,
          courses: ['Python for Beginners', 'Web Development Zero to Hero', 'Data Science with Python', 'Cybersecurity Basics'],
        },
      ],
    },
    qbankQuestions: {
      photo: {
        '0': [
          {
            id: 'qb_photo_1',
            prompt: 'Which aperture setting gives the shallowest depth of field?',
            options: ['f/16', 'f/8', 'f/2.8', 'f/5.6'],
            answerIndex: 2,
            explanation: 'Wider apertures (lower f-numbers) produce shallower depth of field.',
            isPlaceholder: false,
          },
        ],
      },
      python: {
        '0': [
          {
            id: 'qb_python_1',
            prompt: 'What does `len()` return for a list?',
            options: ['The sum', 'The count of items', 'The last index', 'The memory size'],
            answerIndex: 1,
            explanation: 'len() returns the number of elements.',
            isPlaceholder: false,
          },
        ],
      },
    },
    enrollments: {
      enrollments: [
        {
          userId: 'u_sadia',
          courseSlug: 'photo',
          name: 'Sadia Rahman',
          email: 'sadia@demo.com',
          course: 'Photography Masterclass',
          purchased: true,
          progressPct: 72,
          certId: null,
        },
        {
          userId: 'u_rahim',
          courseSlug: 'python',
          name: 'Rahim Khan',
          email: 'rahim@demo.com',
          course: 'Python for Beginners',
          purchased: true,
          progressPct: 100,
          certId: 'CERT-2024-001',
        },
      ],
      students,
      courses: DEMO_COURSES.map((c) => ({ slug: c.slug, title: c.title })),
    },
    users: {
      users: [
        { id: 'u_sadia', name: 'Sadia Rahman', email: 'sadia@demo.com', role: 'student', suspended: false, points: 420, joinedAt: iso(90) },
        { id: 'u_rahim', name: 'Rahim Khan', email: 'rahim@demo.com', role: 'student', suspended: false, points: 890, joinedAt: iso(120) },
        { id: 'u_instructor', name: 'Tanvir Hasan', email: 'instructor@maktechacademy.com', role: 'instructor', suspended: false, points: 0, joinedAt: iso(200) },
        {
          id: 'u_imran_instr',
          name: 'Imran Chowdhury',
          email: 'imran@example.com',
          role: 'instructor',
          suspended: false,
          points: 0,
          joinedAt: iso(28),
        },
        { id: 'u_affiliate', name: 'Nasir Ahmed', email: 'affiliate@demo.com', role: 'affiliate', suspended: false, points: 150, joinedAt: iso(60) },
        { id: 'u_admin', name: 'Admin User', email: 'admin@maktechacademy.com', role: 'admin', suspended: false, points: 0, joinedAt: iso(365) },
        {
          id: 'u_ordermgr',
          name: 'Order Manager',
          email: 'orders@maktechacademy.com',
          role: 'order_manager',
          suspended: false,
          points: 0,
          joinedAt: iso(40),
        },
      ],
    },
    staff: {
      staff: [
        { id: 'st_1', name: 'Admin User', email: 'admin@maktechacademy.com', role: 'superadmin' },
        { id: 'st_2', name: 'Support Agent', email: 'support@maktechacademy.com', role: 'support' },
        { id: 'st_3', name: 'Instructor Demo', email: 'instructor@maktechacademy.com', role: 'instructor' },
        { id: 'st_5', name: 'Imran Chowdhury', email: 'imran@example.com', role: 'instructor' },
        { id: 'st_4', name: 'Order Manager', email: 'orders@maktechacademy.com', role: 'order_manager' },
      ],
      roles,
      matrix,
    },
    orders: {
      orders: [
        {
          orderId: 'ORD-9901',
          customerName: 'Sadia Rahman',
          email: 'sadia@demo.com',
          items: [{ title: 'Photography Masterclass', price: 349000 }],
          total: 349000,
          method: 'bKash',
          status: 'paid',
          createdAt: '2026-07-10T09:14:00.000Z',
          paidAt: '2026-07-10T09:15:22.000Z',
          txnRef: 'BKX7F2A91',
        },
        {
          orderId: 'ORD-9898',
          customerName: 'Rahim Uddin',
          email: 'rahim@demo.com',
          items: [
            { title: 'Python for Beginners', price: 249000 },
            { title: 'Excel for Professionals', price: 250000 },
          ],
          total: 499000,
          method: 'card',
          status: 'refund_requested',
          createdAt: '2026-07-05T14:22:00.000Z',
          paidAt: '2026-07-05T14:22:41.000Z',
          txnRef: 'CARD-88421',
          refundRequest: {
            reason: 'Course content mismatch',
            details:
              'I enrolled for live Python sessions but only recorded videos were available. I would like a refund under the 30-day guarantee.',
            requestedAt: '2026-07-20T11:30:00.000Z',
          },
        },
        {
          orderId: 'ORD-9880',
          customerName: 'Nusrat Jahan',
          email: 'nusrat@demo.com',
          items: [{ title: 'Graphic Design Fundamentals', price: 299000 }],
          total: 299000,
          method: 'bKash',
          status: 'refunded',
          createdAt: '2026-06-18T08:05:00.000Z',
          paidAt: '2026-06-18T08:06:10.000Z',
          txnRef: 'BKX3C8801',
          refundRequest: {
            reason: 'Accidental purchase',
            details: 'Bought the wrong course by mistake on mobile.',
            requestedAt: '2026-06-20T16:45:00.000Z',
          },
          refundedAt: '2026-06-21T10:12:00.000Z',
        },
      ],
    },
    books: {
      books: [
        {
          slug: 'spoken-english',
          title: 'Ghore Boshe Spoken English',
          author: 'MAKTech Academy',
          price: 35000,
          oldPrice: 48000,
          category: 'English',
          stock: 18,
          inStock: true,
          pages: 216,
          createdAt: iso(120),
        },
        {
          slug: 'react-workbook',
          title: 'React in Practice — project workbook',
          author: 'MAKTech Academy',
          price: 42000,
          oldPrice: null,
          category: 'Development',
          stock: 42,
          inStock: true,
          pages: 184,
          createdAt: iso(95),
        },
        {
          slug: 'pandas-bengali',
          title: 'Pandas for Bengali analysts',
          author: 'MAKTech Academy',
          price: 38000,
          oldPrice: 45000,
          category: 'Data Science',
          stock: 31,
          inStock: true,
          pages: 198,
          createdAt: iso(80),
        },
        {
          slug: 'excel-wall-chart',
          title: 'Excel shortcuts wall chart',
          author: 'MAKTech Academy',
          price: 25000,
          oldPrice: null,
          category: 'Office',
          stock: 55,
          inStock: true,
          pages: 2,
          createdAt: iso(70),
        },
        {
          slug: 'typography-sketchbook',
          title: 'Typography sketchbook',
          author: 'MAKTech Academy',
          price: 32000,
          oldPrice: 39000,
          category: 'Design',
          stock: 24,
          inStock: true,
          pages: 128,
          createdAt: iso(60),
        },
        {
          slug: 'resume-pack',
          title: 'Resume templates pack (Bangla + English)',
          author: 'MAKTech Academy',
          price: 29000,
          oldPrice: null,
          category: 'Career',
          stock: 0,
          inStock: false,
          pages: 48,
          createdAt: iso(45),
        },
        {
          slug: 'python-handbook',
          title: 'Python Handbook for beginners',
          author: 'MAKTech Academy',
          price: 45000,
          oldPrice: 52000,
          category: 'Development',
          stock: 36,
          inStock: true,
          pages: 240,
          createdAt: iso(40),
        },
        {
          slug: 'design-workbook',
          title: 'Design Workbook — layout & colour',
          author: 'MAKTech Academy',
          price: 36000,
          oldPrice: null,
          category: 'Design',
          stock: 15,
          inStock: true,
          pages: 156,
          createdAt: iso(38),
        },
        {
          slug: 'sql-cheatsheet',
          title: 'SQL cheatsheet for analysts',
          author: 'MAKTech Academy',
          price: 22000,
          oldPrice: 28000,
          category: 'Data Science',
          stock: 64,
          inStock: true,
          pages: 32,
          createdAt: iso(35),
        },
        {
          slug: 'powerpoint-mastery',
          title: 'PowerPoint mastery workbook',
          author: 'MAKTech Academy',
          price: 28000,
          oldPrice: null,
          category: 'Office',
          stock: 28,
          inStock: true,
          pages: 112,
          createdAt: iso(32),
        },
        {
          slug: 'ielts-writing',
          title: 'IELTS writing task workbook',
          author: 'MAKTech Academy',
          price: 40000,
          oldPrice: 48000,
          category: 'English',
          stock: 12,
          inStock: true,
          pages: 172,
          createdAt: iso(30),
        },
        {
          slug: 'nodejs-projects',
          title: 'Node.js projects — backend guide',
          author: 'MAKTech Academy',
          price: 48000,
          oldPrice: null,
          category: 'Development',
          stock: 22,
          inStock: true,
          pages: 210,
          createdAt: iso(28),
        },
        {
          slug: 'freelance-pricing',
          title: 'Freelance pricing playbook',
          author: 'MAKTech Academy',
          price: 26000,
          oldPrice: 30000,
          category: 'Career',
          stock: 40,
          inStock: true,
          pages: 96,
          createdAt: iso(25),
        },
        {
          slug: 'figma-ui-kit',
          title: 'Figma UI kit reference book',
          author: 'MAKTech Academy',
          price: 34000,
          oldPrice: null,
          category: 'Design',
          stock: 19,
          inStock: true,
          pages: 144,
          createdAt: iso(22),
        },
        {
          slug: 'machine-learning-intro',
          title: 'Machine learning intro (Bangla notes)',
          author: 'MAKTech Academy',
          price: 44000,
          oldPrice: 50000,
          category: 'Data Science',
          stock: 27,
          inStock: true,
          pages: 188,
          createdAt: iso(20),
        },
        {
          slug: 'word-templates',
          title: 'Word templates for offices',
          author: 'MAKTech Academy',
          price: 20000,
          oldPrice: null,
          category: 'Office',
          stock: 50,
          inStock: true,
          pages: 64,
          createdAt: iso(18),
        },
        {
          slug: 'grammar-essentials',
          title: 'Grammar essentials workbook',
          author: 'MAKTech Academy',
          price: 30000,
          oldPrice: 35000,
          category: 'English',
          stock: 33,
          inStock: true,
          pages: 160,
          createdAt: iso(15),
        },
        {
          slug: 'git-github-guide',
          title: 'Git & GitHub practical guide',
          author: 'MAKTech Academy',
          price: 32000,
          oldPrice: null,
          category: 'Development',
          stock: 44,
          inStock: true,
          pages: 136,
          createdAt: iso(12),
        },
      ],
    },
    bookorders: {
      counts: { processing: 2, packed: 1, shipped: 1, delivered: 1 },
      orders: [
        {
          orderId: 'BK-201',
          name: 'Karim Ahmed',
          email: 'karim@demo.com',
          phone: '+8801712345678',
          address: 'House 12, Road 5, Dhanmondi',
          city: 'Dhaka',
          district: 'Dhaka',
          items: [{ title: 'Python Handbook for beginners', qty: 1, unitPrice: 45000 }],
          subtotal: 45000,
          deliveryFee: 6000,
          total: 51000,
          paymentMethod: 'Cash on delivery',
          status: 'pending',
          notes: 'Call before delivery after 6 PM.',
          orderedAt: '2026-07-22T10:30:00.000Z',
        },
        {
          orderId: 'BK-198',
          name: 'Sadia Rahman',
          email: 'sadia@demo.com',
          phone: '+8801812345678',
          address: 'Agrabad Commercial Area, Plot 44',
          city: 'Chittagong',
          district: 'Chittagong',
          items: [
            { title: 'Design Workbook — layout & colour', qty: 2, unitPrice: 36000 },
            { title: 'Typography sketchbook', qty: 1, unitPrice: 32000 },
          ],
          subtotal: 104000,
          deliveryFee: 8000,
          total: 112000,
          paymentMethod: 'Cash on delivery',
          status: 'shipped',
          notes: 'Leave with building security if unavailable.',
          orderedAt: '2026-07-18T14:15:00.000Z',
          shippedAt: '2026-07-20T09:00:00.000Z',
        },
        {
          orderId: 'BK-195',
          name: 'Rahim Uddin',
          email: 'rahim@demo.com',
          phone: '+8801911223344',
          address: 'Zindabazar, Mirza Jahan Ali Road',
          city: 'Sylhet',
          district: 'Sylhet',
          items: [{ title: 'Ghore Boshe Spoken English', qty: 1, unitPrice: 35000 }],
          subtotal: 35000,
          deliveryFee: 7000,
          total: 42000,
          paymentMethod: 'Cash on delivery',
          status: 'processing',
          orderedAt: '2026-07-21T08:45:00.000Z',
        },
        {
          orderId: 'BK-190',
          name: 'Nusrat Jahan',
          email: 'nusrat@demo.com',
          phone: '+8801612345678',
          address: 'Kazir Deuri, Holding 8',
          city: 'Rajshahi',
          district: 'Rajshahi',
          items: [{ title: 'Excel shortcuts wall chart', qty: 3, unitPrice: 25000 }],
          subtotal: 75000,
          deliveryFee: 6000,
          total: 81000,
          paymentMethod: 'Cash on delivery',
          status: 'delivered',
          orderedAt: '2026-07-10T11:20:00.000Z',
          shippedAt: '2026-07-12T10:00:00.000Z',
          deliveredAt: '2026-07-15T16:30:00.000Z',
        },
        {
          orderId: 'BK-188',
          name: 'Tanvir Hasan',
          email: 'tanvir@demo.com',
          phone: '+8801512345678',
          address: 'Sector 7, Uttara',
          city: 'Dhaka',
          district: 'Dhaka',
          items: [{ title: 'React in Practice — project workbook', qty: 1, unitPrice: 42000 }],
          subtotal: 42000,
          deliveryFee: 6000,
          total: 48000,
          paymentMethod: 'Cash on delivery',
          status: 'packed',
          orderedAt: '2026-07-19T17:00:00.000Z',
        },
      ],
    },
    payouts: {
      commissionPct: 30,
      payouts: [
        {
          instructorId: 'ins_admin_1',
          instructor: 'Ayesha Karim',
          email: 'ayesha@maktechacademy.com',
          period: 'Jul 2026',
          gross: 450000,
          platform: 135000,
          instructorShare: 315000,
          status: 'pending',
          paymentMethod: 'bKash',
          payoutAccount: '+8801711000001',
          courses: [
            { title: 'Photography Masterclass', sales: 280000, share: 196000 },
            { title: 'Graphic Design Fundamentals', sales: 170000, share: 119000 },
          ],
        },
        {
          instructorId: 'ins_admin_2',
          instructor: 'Tanvir Hasan',
          email: 'instructor@maktechacademy.com',
          period: 'Jul 2026',
          gross: 320000,
          platform: 96000,
          instructorShare: 224000,
          status: 'paid',
          paymentMethod: 'Bank transfer',
          payoutAccount: 'Dutch-Bangla Bank · ****4821',
          courses: [
            { title: 'Python for Beginners', sales: 210000, share: 147000 },
            { title: 'Web Development Zero to Hero', sales: 110000, share: 77000 },
          ],
          paidAt: '2026-07-05T11:20:00.000Z',
          reference: 'PAY-20260705-002',
        },
        {
          instructorId: 'app_2',
          instructor: 'Imran Chowdhury',
          email: 'imran@example.com',
          period: 'Jul 2026',
          gross: 185000,
          platform: 55500,
          instructorShare: 129500,
          status: 'processing',
          paymentMethod: 'bKash',
          payoutAccount: '+8801811000003',
          courses: [{ title: 'Excel for Professionals', sales: 185000, share: 129500 }],
        },
        {
          instructorId: 'ins_admin_3',
          instructor: 'Sadia Rahman',
          email: 'sadia.teach@maktechacademy.com',
          period: 'Jun 2026',
          gross: 96000,
          platform: 28800,
          instructorShare: 67200,
          status: 'paid',
          paymentMethod: 'bKash',
          payoutAccount: '+8801911000004',
          courses: [{ title: 'Digital Marketing Bootcamp', sales: 96000, share: 67200 }],
          paidAt: '2026-06-28T14:00:00.000Z',
          reference: 'PAY-20260628-004',
        },
      ],
    },
    coupons: {
      coupons: [
        {
          code: 'WELCOME10',
          discountType: 'percent',
          value: 10,
          courseSlug: null,
          courseTitle: null,
          audience: 'public',
          expiresAt: '2026-12-31',
          active: true,
          createdAt: iso(60),
        },
        {
          code: 'FLASH25',
          discountType: 'percent',
          value: 25,
          courseSlug: null,
          courseTitle: null,
          audience: 'public',
          expiresAt: '2026-07-20',
          active: true,
          createdAt: iso(20),
        },
        {
          code: 'PHOTO500',
          discountType: 'flat',
          value: 50000,
          courseSlug: 'photo',
          courseTitle: 'Photography Masterclass',
          audience: 'public',
          expiresAt: '2026-09-30',
          active: true,
          createdAt: iso(15),
        },
        {
          code: 'PYTHON15',
          discountType: 'percent',
          value: 15,
          courseSlug: 'python',
          courseTitle: 'Python for Beginners',
          audience: 'public',
          expiresAt: '2026-06-01',
          active: true,
          createdAt: iso(90),
        },
        {
          code: 'OLDDEAL',
          discountType: 'percent',
          value: 20,
          courseSlug: null,
          courseTitle: null,
          audience: 'public',
          expiresAt: '2026-08-15',
          active: false,
          createdAt: iso(40),
        },
        {
          code: 'AFFPHOTO20',
          discountType: 'percent',
          value: 20,
          courseSlug: 'photo',
          courseTitle: 'Photography Masterclass',
          audience: 'affiliate',
          expiresAt: '2026-12-31',
          active: true,
          createdAt: iso(5),
        },
        {
          code: 'AFFALL10',
          discountType: 'percent',
          value: 10,
          courseSlug: null,
          courseTitle: null,
          audience: 'affiliate',
          expiresAt: '2026-12-31',
          active: true,
          createdAt: iso(5),
        },
      ],
    },
    assignments: {
      reviewQueue: 3,
      assignments: [
        {
          id: 'asg_1',
          title: 'Exposure triangle exercise',
          brief: 'Submit 3 photos demonstrating ISO, aperture, shutter speed.',
          instructionsHtml:
            '<p>Submit <strong>3 photos</strong> that demonstrate:</p><ul><li>ISO control</li><li>Aperture depth of field</li><li>Shutter speed motion</li></ul><p>Write a short note under each photo explaining your settings.</p>',
          course: 'Photography Masterclass',
          courseSlug: 'photo',
          dueDate: iso(-7),
          submissions: 12,
          reviewed: 9,
          createdAt: iso(20),
        },
        {
          id: 'asg_2',
          title: 'Build a todo CLI',
          brief: 'Python script with add/list/complete commands.',
          instructionsHtml:
            '<p>Build a small <em>Python CLI</em> with these commands:</p><ol><li><code>add</code> — create a task</li><li><code>list</code> — show tasks</li><li><code>complete</code> — mark done</li></ol><p>Push your repo link when finished.</p>',
          course: 'Python for Beginners',
          courseSlug: 'python',
          dueDate: iso(3),
          submissions: 8,
          reviewed: 5,
          createdAt: iso(12),
        },
      ],
    },
    content: {
      articles: [
        { slug: 'how-to-learn-python', title: 'How to Learn Python in 30 Days', category: 'Programming', author: 'Tanvir Hasan', publishedAt: iso(14) },
        { slug: 'freelance-tips', title: '5 Freelancing Tips for Beginners', category: 'Career', author: 'Ayesha Karim', publishedAt: iso(21) },
      ],
    },
    downloads: {
      resources: [
        { slug: 'python-cheatsheet', title: 'Python Cheatsheet PDF', category: 'Programming', type: 'PDF', downloads: 1240 },
        { slug: 'design-icons', title: 'Free Icon Pack', category: 'Design', type: 'ZIP', downloads: 890 },
      ],
      leads: [
        { name: 'Mina Akter', email: 'mina@example.com', phone: '+8801711111111', wanted: 'Python Cheatsheet PDF', source: 'download', createdAt: iso(2) },
      ],
    },
    qa: {
      threads: [
        {
          id: 'qa_1',
          course: 'Photography Masterclass',
          question: 'What lens do you recommend for portraits on a budget?',
          user: { name: 'Sadia Rahman', email: 'sadia@demo.com' },
          replies: [],
        },
        {
          id: 'qa_2',
          course: 'Python for Beginners',
          question: 'Difference between list and tuple?',
          user: { name: 'Rahim Khan', email: 'rahim@demo.com' },
          replies: [{ id: 'qr1', body: 'Lists are mutable; tuples are immutable.', staff: true, user: { name: 'Instructor' } }],
        },
      ],
    },
    tickets: {
      counts: {
        open: tickets.filter((t) => t.status === 'open').length,
        progress: tickets.filter((t) => t.status === 'progress').length,
        resolved: tickets.filter((t) => t.status === 'resolved').length,
      },
      tickets,
    },
    reviews: {
      reviews: [
        {
          id: 'rev_1',
          name: 'Sadia Rahman',
          email: 'sadia@demo.com',
          rating: 5,
          body: 'Excellent course! Very practical lighting drills and clear feedback.',
          course: 'Photography Masterclass',
          courseSlug: 'photo',
          status: 'published',
          createdAt: iso(5),
        },
        {
          id: 'rev_2',
          name: 'Karim Ahmed',
          email: 'karim@demo.com',
          rating: 4,
          body: 'Loved the composition modules. Would like more night photography tips.',
          course: 'Photography Masterclass',
          courseSlug: 'photo',
          status: 'published',
          createdAt: iso(12),
        },
        {
          id: 'rev_3',
          name: 'Nusrat Jahan',
          email: 'nusrat@demo.com',
          rating: 2,
          body: 'Camera settings section felt rushed. Please expand ISO examples.',
          course: 'Photography Masterclass',
          courseSlug: 'photo',
          status: 'flagged',
          createdAt: iso(18),
        },
        {
          id: 'rev_4',
          name: 'Rahim Khan',
          email: 'rahim@demo.com',
          rating: 4,
          body: 'Great intro to Python — exercises are beginner-friendly.',
          course: 'Python for Beginners',
          courseSlug: 'python',
          status: 'published',
          createdAt: iso(10),
        },
        {
          id: 'rev_5',
          name: 'Sadia Rahman',
          email: 'sadia@demo.com',
          rating: 5,
          body: 'Clear explanations and useful practice notebooks.',
          course: 'Python for Beginners',
          courseSlug: 'python',
          status: 'published',
          createdAt: iso(22),
        },
        {
          id: 'rev_6',
          name: 'Imran Hossain',
          email: 'imran@demo.com',
          rating: 3,
          body: 'Good course but some videos buffer a lot on mobile data.',
          course: 'Python for Beginners',
          courseSlug: 'python',
          status: 'hidden',
          createdAt: iso(28),
        },
        {
          id: 'rev_7',
          name: 'Farzana Akter',
          email: 'farzana@demo.com',
          rating: 5,
          body: 'Pivot tables finally make sense. Office templates are gold.',
          course: 'Excel for Professionals',
          courseSlug: 'excel',
          status: 'published',
          createdAt: iso(7),
        },
        {
          id: 'rev_8',
          name: 'Rahim Khan',
          email: 'rahim@demo.com',
          rating: 4,
          body: 'VLOOKUP/XLOOKUP section alone was worth it.',
          course: 'Excel for Professionals',
          courseSlug: 'excel',
          status: 'published',
          createdAt: iso(15),
        },
        {
          id: 'rev_9',
          name: 'Nusrat Jahan',
          email: 'nusrat@demo.com',
          rating: 5,
          body: 'Project-based lessons helped me land my first freelance gig.',
          course: 'Web Development Zero to Hero',
          courseSlug: 'web-dev',
          status: 'published',
          createdAt: iso(3),
        },
        {
          id: 'rev_10',
          name: 'Karim Ahmed',
          email: 'karim@demo.com',
          rating: 1,
          body: 'This is spam — buy cheap certificates at fake-site.xyz!!!',
          course: 'Web Development Zero to Hero',
          courseSlug: 'web-dev',
          status: 'flagged',
          createdAt: iso(2),
        },
        {
          id: 'rev_11',
          name: 'Sadia Rahman',
          email: 'sadia@demo.com',
          rating: 5,
          body: 'Colour theory and typography modules are excellent.',
          course: 'Graphic Design Fundamentals',
          courseSlug: 'graphic-design',
          status: 'published',
          createdAt: iso(9),
        },
        {
          id: 'rev_12',
          name: 'Farzana Akter',
          email: 'farzana@demo.com',
          rating: 4,
          body: 'Solid foundations. Would love a Figma follow-up course.',
          course: 'UI/UX Design Essentials',
          courseSlug: 'ui-ux',
          status: 'published',
          createdAt: iso(14),
        },
      ],
    },
    instructors: {
      instructors: [
        {
          id: 'ins_admin_1',
          name: 'Ayesha Karim',
          email: 'ayesha@maktechacademy.com',
          source: 'admin',
          status: 'active',
          courseAccess: 'all',
          courseSlugs: [],
          courseTitles: [],
          topic: null,
          suspended: false,
          createdAt: iso(120),
          approvedAt: iso(120),
        },
        {
          id: 'ins_admin_2',
          name: 'Tanvir Hasan',
          email: 'instructor@maktechacademy.com',
          source: 'admin',
          status: 'active',
          courseAccess: 'selected',
          courseSlugs: ['python', 'web-dev', 'data-science'],
          courseTitles: ['Python for Beginners', 'Web Development Zero to Hero', 'Data Science with Python'],
          topic: null,
          phone: '01711001100',
          headline: 'Full-stack instructor',
          bio: 'Teaches Python, web development and data science at MAKTech Academy.',
          suspended: false,
          createdAt: iso(90),
          approvedAt: iso(90),
        },
        {
          id: 'app_1',
          name: 'Farhana Islam',
          email: 'farhana@example.com',
          source: 'application',
          status: 'pending',
          courseAccess: 'own',
          courseSlugs: [],
          courseTitles: [],
          topic: 'Data Analytics',
          suspended: false,
          createdAt: iso(3),
          approvedAt: null,
        },
        {
          id: 'app_4',
          name: 'Mehedi Hasan',
          email: 'mehedi@example.com',
          source: 'application',
          status: 'pending',
          courseAccess: 'own',
          courseSlugs: [],
          courseTitles: [],
          topic: 'Digital Marketing',
          suspended: false,
          createdAt: iso(1),
          approvedAt: null,
        },
        {
          id: 'app_2',
          name: 'Imran Chowdhury',
          email: 'imran@example.com',
          source: 'application',
          status: 'active',
          courseAccess: 'own',
          courseSlugs: ['excel', 'web-dev', 'video-editing', 'cybersecurity'],
          courseTitles: [
            'Excel for Professionals',
            'Web Development Zero to Hero',
            'Video Editing with Premiere',
            'Cybersecurity Basics',
          ],
          topic: 'Video Editing',
          phone: '01811000003',
          headline: 'Creator & course mentor',
          bio: 'Helps learners with Excel, web development, video editing and cybersecurity.',
          suspended: false,
          createdAt: iso(30),
          approvedAt: iso(28),
          paymentMethods: {
            bank: null,
            bkash: '01811000003',
            nagad: null,
            rocket: null,
          },
          availableBalance: 129500,
        },
        {
          id: 'app_3',
          name: 'Rifat Hasan',
          email: 'rifat@example.com',
          source: 'application',
          status: 'rejected',
          courseAccess: 'own',
          courseSlugs: [],
          courseTitles: [],
          topic: 'Spoken English',
          suspended: false,
          createdAt: iso(20),
          approvedAt: null,
        },
        {
          id: 'ins_admin_3',
          name: 'Sadia Rahman',
          email: 'sadia.teach@maktechacademy.com',
          source: 'admin',
          status: 'active',
          courseAccess: 'selected',
          courseSlugs: ['photo', 'graphic-design'],
          courseTitles: ['Photography Masterclass', 'Graphic Design Fundamentals'],
          topic: null,
          suspended: true,
          createdAt: iso(45),
          approvedAt: iso(45),
        },
      ],
    },
    leads: {
      leads: [
        { id: 'lead_1', name: 'Rashed Karim', company: 'TechCorp BD', email: 'hr@techcorp.bd', teamSize: '50–100', status: 'new', createdAt: iso(1) },
        { id: 'lead_2', name: 'Nabila Chowdhury', company: 'StartupXYZ', email: 'ceo@startupxyz.com', teamSize: '10–25', status: 'contacted', createdAt: iso(8) },
        { id: 'lead_3', name: 'Imran Hossain', company: 'Pathao Learning', email: 'imran@pathao.demo', teamSize: '26–100 people', status: 'visited', createdAt: iso(4) },
        { id: 'lead_4', name: 'Farzana Akter', company: 'BRAC Soft', email: 'lnd@bracsoft.demo', teamSize: '100+ people', status: 'new', createdAt: iso(2) },
        { id: 'lead_5', name: 'Tanvir Ahmed', company: 'ShopUp Academy', email: 'tanvir@shopup.demo', teamSize: '11–25 people', status: 'closed', createdAt: iso(20) },
        { id: 'lead_6', name: 'Sadia Rahman', company: 'Creative Agency BD', email: 'sadia@creative.demo', teamSize: '5–10 people', status: 'contacted', createdAt: iso(6) },
        { id: 'lead_7', name: 'Karim Ahmed', company: 'bKash Training Hub', email: 'karim@bkash.demo', teamSize: '100+ people', status: 'visited', createdAt: iso(3) },
        { id: 'lead_8', name: 'Nusrat Jahan', company: 'Grameenphone IT', email: 'nusrat@gp.demo', teamSize: '26–100 people', status: 'new', createdAt: iso(0) },
        { id: 'lead_9', name: 'Rahim Khan', company: 'SSL Wireless', email: 'rahim@ssl.demo', teamSize: '11–25 people', status: 'contacted', createdAt: iso(9) },
        { id: 'lead_10', name: 'Lamia Sultana', company: 'Daraz Academy', email: 'lamia@daraz.demo', teamSize: '50–100', status: 'visited', createdAt: iso(5) },
        { id: 'lead_11', name: 'Arif Hossain', company: 'Foodpanda Ops', email: 'arif@foodpanda.demo', teamSize: '5–10 people', status: 'new', createdAt: iso(7) },
        { id: 'lead_12', name: 'Mehedi Hasan', company: 'IDLC Finance', email: 'mehedi@idlc.demo', teamSize: '100+ people', status: 'closed', createdAt: iso(15) },
        { id: 'lead_13', name: 'Ayesha Begum', company: 'Robi Digital', email: 'ayesha@robi.demo', teamSize: '26–100 people', status: 'contacted', createdAt: iso(11) },
        { id: 'lead_14', name: 'Shanjid Ahmed', company: 'Brain Station 23', email: 'shanjid@bs23.demo', teamSize: '11–25 people', status: 'visited', createdAt: iso(12) },
        { id: 'lead_15', name: 'Rifat Hasan', company: 'BJIT Group', email: 'rifat@bjit.demo', teamSize: '50–100', status: 'new', createdAt: iso(10) },
      ],
    },
    freeresources: {
      resources: [
        {
          id: 'fr_1',
          title: 'Python Cheatsheet',
          description: 'Quick reference for beginners — syntax, lists, dicts and loops.',
          category: 'Programming',
          fileName: 'python-cheatsheet.pdf',
          pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
          downloads: 1240,
          createdAt: iso(40),
          updatedAt: iso(10),
        },
        {
          id: 'fr_2',
          title: 'Excel Shortcuts Wall Chart',
          description: 'Printable keyboard shortcuts for Windows Excel.',
          category: 'Office',
          fileName: 'excel-shortcuts.pdf',
          pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
          downloads: 890,
          createdAt: iso(35),
          updatedAt: iso(12),
        },
        {
          id: 'fr_3',
          title: 'UI Colour Theory Guide',
          description: 'Palette tips and contrast rules for product designers.',
          category: 'Design',
          fileName: 'colour-theory.pdf',
          pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
          downloads: 560,
          createdAt: iso(28),
          updatedAt: iso(8),
        },
        {
          id: 'fr_4',
          title: 'Freelance Pricing Playbook',
          description: 'How to price projects in the Bangladesh market.',
          category: 'Career',
          fileName: 'freelance-pricing.pdf',
          pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
          downloads: 720,
          createdAt: iso(22),
          updatedAt: iso(5),
        },
        {
          id: 'fr_5',
          title: 'SQL Cheatsheet',
          description: 'SELECT, JOIN, GROUP BY and window functions at a glance.',
          category: 'Data',
          fileName: 'sql-cheatsheet.pdf',
          pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
          downloads: 980,
          createdAt: iso(18),
          updatedAt: iso(3),
        },
        {
          id: 'fr_6',
          title: 'Spoken English Phrase Pack',
          description: 'Daily conversation phrases with Bangla meanings.',
          category: 'Language',
          fileName: 'spoken-english.pdf',
          pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
          downloads: 1510,
          createdAt: iso(14),
          updatedAt: iso(2),
        },
      ],
    },
    pdfleads: {
      total: 156,
      bySource: { course_page: 89, blog: 42, resources: 25 },
      leads: [
        { name: 'Arif Hossain', email: 'arif@example.com', phone: null, wanted: 'Python Cheatsheet', source: 'resources', createdAt: iso(1) },
        { name: 'Lamia Sultana', email: 'lamia@example.com', phone: '+8801722222222', wanted: 'Design Guide', source: 'blog', createdAt: iso(4) },
      ],
    },
    counselling: {
      bookings: [
        { id: 'cb_1', name: 'Nusrat Jahan', phone: '+8801833333333', email: 'nusrat@demo.com', goal: 'Career switch to tech', slot: 'Sat 3pm', status: 'pending', createdAt: iso(0) },
        { id: 'cb_2', name: 'Karim Ahmed', phone: '+8801744444444', email: 'karim@demo.com', goal: 'Freelancing roadmap', slot: 'Sun 11am', status: 'called', createdAt: iso(2) },
      ],
    },
    affiliates: {
      stats: { affiliates: 12, referredEnrolments: 86, commissionEarned: 412500, pendingWithdrawals: 8 },
      sales: [
        {
          id: 'sale_nasir_1',
          affiliateId: 'aff_nasir',
          affiliateName: 'Nasir Ahmed',
          affiliateCode: 'MAK-NA01',
          courseSlug: 'photo',
          courseTitle: 'Photography Masterclass',
          orderAmount: 299000,
          commission: 35880,
          commissionPct: 12,
          couponCode: 'AFFPHOTO20',
          createdAt: iso(18),
        },
        {
          id: 'sale_nasir_2',
          affiliateId: 'aff_nasir',
          affiliateName: 'Nasir Ahmed',
          affiliateCode: 'MAK-NA01',
          courseSlug: 'python',
          courseTitle: 'Python for Beginners',
          orderAmount: 349000,
          commission: 41880,
          commissionPct: 12,
          couponCode: null,
          createdAt: iso(12),
        },
        {
          id: 'sale_nasir_3',
          affiliateId: 'aff_nasir',
          affiliateName: 'Nasir Ahmed',
          affiliateCode: 'MAK-NA01',
          courseSlug: 'excel',
          courseTitle: 'Excel for Professionals',
          orderAmount: 399000,
          commission: 47880,
          commissionPct: 12,
          couponCode: 'AFFALL10',
          createdAt: iso(6),
        },
        {
          id: 'sale_rahim_1',
          affiliateId: 'aff_1',
          affiliateName: 'Rahim Khan',
          affiliateCode: 'MAK-RH01',
          courseSlug: 'python',
          courseTitle: 'Python for Beginners',
          orderAmount: 349000,
          commission: 41880,
          commissionPct: 12,
          couponCode: null,
          createdAt: iso(10),
        },
      ],
      withdrawals: [
        {
          id: 'wd_nasir_1',
          name: 'Nasir Ahmed',
          email: 'affiliate@demo.com',
          phone: '01719990001',
          code: 'MAK-NA01',
          amount: 45000,
          method: 'bkash',
          account: '01719990001',
          status: 'paid',
          requestedAt: iso(20),
        },
        {
          id: 'wd_nasir_2',
          name: 'Nasir Ahmed',
          email: 'affiliate@demo.com',
          phone: '01719990001',
          code: 'MAK-NA01',
          amount: 30000,
          method: 'bkash',
          account: '01719990001',
          status: 'pending',
          requestedAt: iso(2),
        },
        {
          id: 'wd_1',
          name: 'Rahim Khan',
          email: 'rahim.aff@demo.com',
          phone: '01711000001',
          code: 'MAK-RH01',
          amount: 4500,
          method: 'bkash',
          account: '01711000001',
          status: 'pending',
          requestedAt: iso(1),
        },
        {
          id: 'wd_2',
          name: 'Nusrat Jahan',
          email: 'nusrat.aff@demo.com',
          phone: '01812000002',
          code: 'MAK-NJ02',
          amount: 3200,
          method: 'nagad',
          account: '01812000002',
          status: 'pending',
          requestedAt: iso(2),
        },
        {
          id: 'wd_3',
          name: 'Sadia Rahman',
          email: 'sadia.aff@demo.com',
          phone: '01913000003',
          code: 'MAK-SR03',
          amount: 2800,
          method: 'bkash',
          account: '01913000003',
          status: 'pending',
          requestedAt: iso(3),
        },
        {
          id: 'wd_4',
          name: 'Karim Ahmed',
          email: 'karim.aff@demo.com',
          phone: '01614000004',
          code: 'MAK-KA04',
          amount: 5100,
          method: 'nagad',
          account: '01614000004',
          status: 'pending',
          requestedAt: iso(0),
        },
        {
          id: 'wd_5',
          name: 'Imran Hossain',
          email: 'imran.aff@demo.com',
          phone: '01716000006',
          code: 'MAK-IH06',
          amount: 1200,
          method: 'bkash',
          account: '01716000006',
          status: 'pending',
          requestedAt: iso(1),
        },
        {
          id: 'wd_6',
          name: 'Lamia Sultana',
          email: 'lamia.aff@demo.com',
          phone: '01817000007',
          code: 'MAK-LS07',
          amount: 900,
          method: 'nagad',
          account: '01817000007',
          status: 'pending',
          requestedAt: iso(4),
        },
        {
          id: 'wd_7',
          name: 'Mehedi Hasan',
          email: 'mehedi.aff@demo.com',
          phone: '01918000008',
          code: 'MAK-MH08',
          amount: 2100,
          method: 'bkash',
          account: '01918000008',
          status: 'pending',
          requestedAt: iso(2),
        },
        {
          id: 'wd_8',
          name: 'Nabila Chowdhury',
          email: 'nabila.aff@demo.com',
          phone: '01822000012',
          code: 'MAK-NC12',
          amount: 1700,
          method: 'nagad',
          account: '01822000012',
          status: 'pending',
          requestedAt: iso(5),
        },
        {
          id: 'wd_9',
          name: 'Farzana Akter',
          email: 'farzana.aff@demo.com',
          phone: '01515000005',
          code: 'MAK-FA05',
          amount: 1900,
          method: 'bkash',
          account: '01515000005',
          status: 'paid',
          requestedAt: iso(12),
        },
      ],
      affiliates: [
        {
          id: 'aff_nasir',
          name: 'Nasir Ahmed',
          email: 'affiliate@demo.com',
          phone: '01719990001',
          code: 'MAK-NA01',
          clicks: 214,
          signups: 17,
          earned: 245000,
          available: 128000,
          joinedAt: iso(60),
          status: 'active',
          paymentMethods: {
            bank: null,
            bkash: '01719990001',
            nagad: null,
            rocket: null,
          },
          bio: 'Affiliate partner promoting MAKTech Academy courses across Facebook and YouTube.',
        },
        {
          id: 'aff_1',
          name: 'Rahim Khan',
          email: 'rahim.aff@demo.com',
          phone: '01711000001',
          code: 'MAK-RH01',
          clicks: 186,
          signups: 14,
          earned: 18200,
          available: 4500,
          joinedAt: iso(45),
          status: 'active',
        },
        {
          id: 'aff_2',
          name: 'Nusrat Jahan',
          email: 'nusrat.aff@demo.com',
          phone: '01812000002',
          code: 'MAK-NJ02',
          clicks: 142,
          signups: 11,
          earned: 14500,
          available: 3200,
          joinedAt: iso(40),
          status: 'active',
        },
        {
          id: 'aff_3',
          name: 'Sadia Rahman',
          email: 'sadia.aff@demo.com',
          phone: '01913000003',
          code: 'MAK-SR03',
          clicks: 98,
          signups: 8,
          earned: 9800,
          available: 2800,
          joinedAt: iso(38),
          status: 'active',
        },
        {
          id: 'aff_4',
          name: 'Karim Ahmed',
          email: 'karim.aff@demo.com',
          phone: '01614000004',
          code: 'MAK-KA04',
          clicks: 210,
          signups: 16,
          earned: 22100,
          available: 5100,
          joinedAt: iso(55),
          status: 'active',
        },
        {
          id: 'aff_5',
          name: 'Farzana Akter',
          email: 'farzana.aff@demo.com',
          phone: '01515000005',
          code: 'MAK-FA05',
          clicks: 76,
          signups: 6,
          earned: 7400,
          available: 0,
          joinedAt: iso(30),
          status: 'active',
        },
        {
          id: 'aff_6',
          name: 'Imran Hossain',
          email: 'imran.aff@demo.com',
          phone: '01716000006',
          code: 'MAK-IH06',
          clicks: 64,
          signups: 5,
          earned: 6200,
          available: 1200,
          joinedAt: iso(28),
          status: 'active',
        },
        {
          id: 'aff_7',
          name: 'Lamia Sultana',
          email: 'lamia.aff@demo.com',
          phone: '01817000007',
          code: 'MAK-LS07',
          clicks: 55,
          signups: 4,
          earned: 5100,
          available: 900,
          joinedAt: iso(22),
          status: 'active',
        },
        {
          id: 'aff_8',
          name: 'Mehedi Hasan',
          email: 'mehedi.aff@demo.com',
          phone: '01918000008',
          code: 'MAK-MH08',
          clicks: 120,
          signups: 9,
          earned: 11200,
          available: 2100,
          joinedAt: iso(50),
          status: 'active',
        },
        {
          id: 'aff_9',
          name: 'Ayesha Begum',
          email: 'ayesha.aff@demo.com',
          phone: '01619000009',
          code: 'MAK-AB09',
          clicks: 33,
          signups: 2,
          earned: 2800,
          available: 400,
          joinedAt: iso(18),
          status: 'active',
        },
        {
          id: 'aff_10',
          name: 'Tanvir Ahmed',
          email: 'tanvir.aff@demo.com',
          phone: '01520000010',
          code: 'MAK-TA10',
          clicks: 41,
          signups: 3,
          earned: 3600,
          available: 600,
          joinedAt: iso(15),
          status: 'active',
        },
        {
          id: 'aff_11',
          name: 'Rifat Hasan',
          email: 'rifat.aff@demo.com',
          phone: '01721000011',
          code: 'MAK-RH11',
          clicks: 28,
          signups: 1,
          earned: 1500,
          available: 1500,
          joinedAt: iso(10),
          status: 'suspended',
        },
        {
          id: 'aff_12',
          name: 'Nabila Chowdhury',
          email: 'nabila.aff@demo.com',
          phone: '01822000012',
          code: 'MAK-NC12',
          clicks: 88,
          signups: 7,
          earned: 8900,
          available: 1700,
          joinedAt: iso(35),
          status: 'active',
        },
      ],
    },
    live: {
      sessions: [
        { id: 'live_1', course: 'Photography Masterclass', title: 'Live Q&A: Portrait Lighting', startsAt: iso(-1) },
      ],
      liveCourses: DEMO_COURSES.filter((_, i) => i % 3 === 0).map((c) => ({ slug: c.slug, title: c.title })),
    },
    masterclass: {
      webinars: [
        { slug: 'ai-future', title: 'The Future of AI in Bangladesh', category: 'Technology', type: 'Masterclass', startsAt: iso(7), signups: 340, isRecorded: false },
        { slug: 'freelance-2024', title: 'Freelancing Masterclass 2024', category: 'Career', type: 'Webinar', startsAt: null, signups: 890, isRecorded: true },
      ],
    },
    certs: {
      certificates: [
        { certId: 'CERT-2024-001', holder: 'Rahim Khan', course: 'Python for Beginners', issuedAt: iso(20), revoked: false },
        { certId: 'CERT-2024-002', holder: 'Sadia Rahman', course: 'Excel for Professionals', issuedAt: iso(45), revoked: false },
      ],
    },
    announce: {
      announcements: [
        {
          id: 'ann_1',
          title: 'New courses this month',
          body: 'Check out our latest programming and design courses. Enrol this week and get early-bird access to live Q&A sessions with instructors.',
          createdAt: iso(5),
        },
        {
          id: 'ann_2',
          title: 'Maintenance window — Sunday night',
          body: 'We will run scheduled maintenance on Sunday from 1:00 AM to 3:00 AM BST. Live classes already booked will not be affected.',
          createdAt: iso(2),
        },
        {
          id: 'ann_3',
          title: 'hello',
          body: 'Short test announcement for the inbox.',
          createdAt: iso(0),
        },
      ],
      promotions: [
        {
          id: 'promo_1',
          text: 'Enrol now — 25% off with FLASH25',
          active: true,
          createdAt: iso(3),
        },
        {
          id: 'promo_2',
          text: 'Eid Sale — up to 40% OFF on selected live batches',
          active: false,
          createdAt: iso(20),
        },
      ],
      banner: { id: 'promo_1', text: 'Enrol now — 25% off with FLASH25', active: true },
    },
    settings: {
      settings: { regOpen: true, maintenance: false, commissionPct: 30, affiliateCommissionPct: 12, supportEmail: 'support@maktechacademy.com' },
    },
    audit: {
      entries: [
        { action: 'settings.update', meta: { commissionPct: 30 }, actor: 'admin@maktechacademy.com', role: 'superadmin', createdAt: iso(30) },
        { action: 'course.publish', meta: { slug: 'photo' }, actor: 'admin@maktechacademy.com', role: 'superadmin', createdAt: iso(60) },
      ],
    },
  };
}
